import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Text,
  tokens
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { UsageStats } from '../types';
import { apiService } from '../services/api.service';
import { telemetryService } from '../services/telemetry.service';
import '../styles/components/subscription-card.css';

interface SubscriptionCardProps {
  onLicenseActivated?: () => void;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ onLicenseActivated }) => {
   const { t } = useTranslation('common');
   const [stats, setStats] = useState<UsageStats | null>(null);
   const [loading, setLoading] = useState(true);
   const [licenseKey, setLicenseKey] = useState('');
   const [activating, setActivating] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState(false);
   const [currentLicenseKey, setCurrentLicenseKey] = useState<string | null>(null);
   const [tenantData, setTenantData] = useState<any>(null);
   const [unsubscribing, setUnsubscribing] = useState(false);
   const [unsubscribeSuccess, setUnsubscribeSuccess] = useState(false);
   const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
   const [loadingPortal, setLoadingPortal] = useState(false);
   const [subscribing, setSubscribing] = useState(false);
   const [isDesktop, setIsDesktop] = useState<boolean>(() => {
     if (typeof window === 'undefined') return true;
     const ua = navigator.userAgent || '';
     const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(ua);
     return !isMobileUA && window.innerWidth >= 1024;
   });

  useEffect(() => {
    loadUsageStats();
    loadTenantData();
  }, []);

  // Detect desktop vs mobile/tablet to conditionally show Stripe payment button
  useEffect(() => {
    const checkDesktop = () => {
      const ua = navigator.userAgent || '';
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(ua);
      setIsDesktop(!isMobileUA && window.innerWidth >= 1024);
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // ✨ Auto-refresh when subscription status changes (e.g., after Unsubscribe webhook)
  useEffect(() => {
    if (!tenantData) return;

    // Set up polling to refresh tenant data every 5 seconds
    // This ensures the UI updates when the webhook changes subscription status
    const pollInterval = setInterval(() => {
      loadTenantData();
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [tenantData?.subscriptionStatus]);

  const loadUsageStats = async () => {
    try {
      setLoading(true);
      const response = await (apiService as any).getUsageStats();
      if (response.success && response.data) {
        setStats(response.data);
        telemetryService.capture('subscription_usage_viewed', 'subscription-card', {
          properties: {
            plan: response.data.plan || 'free',
            used_requests: response.data.currentMonth?.used || 0,
            total_limit: response.data.currentMonth?.limit || 0,
            usage_percent: response.data.currentMonth?.percentage || 0,
          }
        });
        if (response.data.plan === 'free') {
          telemetryService.capture('subscription_free_plan_detected', 'subscription-card', {
            properties: {
              current_plan: 'free',
            }
          });
        }
      }
    } catch (err) {
      console.error('Failed to load usage stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTenantData = async () => {
    try {
      const response = await (apiService as any).getCurrentTenantInfo();
      if (response.success && response.data) {
        setTenantData(response.data);
        // ✨ BYOL → Marketplace: Only show BYOL license if no active marketplace subscription
        // AND subscription is not cancelled
        if (
          response.data.licenseKey &&
          !response.data.subscriptionId &&
          response.data.subscriptionStatus !== 'cancelled'
        ) {
          setCurrentLicenseKey(response.data.licenseKey);
        } else {
          // Clear BYOL license if marketplace subscription exists or is cancelled
          setCurrentLicenseKey(null);
        }
      }
    } catch (err) {
      console.error('Failed to load tenant data:', err);
    }
  };

   const handleActivateLicense = async () => {
     if (!licenseKey.trim()) {
       setError(t('subscription.licenseActivation.errorEmpty'));
       return;
     }

     setActivating(true);
     setError(null);

     try {
       const response = await (apiService as any).activateLicense(licenseKey);
       if (response.success) {
         setSuccess(true);
         setLicenseKey('');
         telemetryService.capture('subscription_license_activated', 'subscription-card', {
           properties: {
             plan_activated: response.data?.plan || 'premium',
           }
         });
         
         // Reload stats after successful activation
         setTimeout(() => {
           loadUsageStats();
           if (onLicenseActivated) {
             onLicenseActivated();
           }
         }, 1500);
       } else {
         setError(response.error || t('subscription.licenseActivation.errorInvalid'));
         telemetryService.capture('subscription_license_activation_failed', 'subscription-card', {
           properties: {
             error_message: response.error || t('subscription.licenseActivation.errorInvalid'),
           }
         });
       }
     } catch (err) {
       setError(t('subscription.licenseActivation.errorActivation'));
       telemetryService.capture('subscription_license_activation_failed', 'subscription-card', {
         properties: {
           error_message: t('subscription.licenseActivation.errorActivation'),
         }
       });
     } finally {
       setActivating(false);
     }
   };

   const handleUnsubscribe = async () => {
      // Allow unsubscribe for:
      // 1. Marketplace subscriptions (subscriptionId) - any status
      // 2. Expired BYOL licenses (currentLicenseKey + isExpired)
      // 3. Expired or suspended Stripe subscriptions (stripeSubscriptionId + expired/suspended)
      const hasMarketplaceSubscription = tenantData?.subscriptionId;
      const hasExpiredByolLicense = currentLicenseKey && isExpired;
      const hasExpiredOrSuspendedStripeSubscription = 
        tenantData?.stripeSubscriptionId && (isExpired || tenantData?.subscriptionStatus === 'suspended');
      
      if (!hasMarketplaceSubscription && !hasExpiredByolLicense && !hasExpiredOrSuspendedStripeSubscription) {
        setError(t('subscription.unsubscribeModal.errorNoSubscription'));
        return;
      }

     setUnsubscribing(true);
     setError(null);

     telemetryService.capture('subscription_marketplace_unsubscribe_initiated', 'subscription-card', {
       properties: {
         plan: stats?.plan || 'unknown',
       }
     });

       try {
        // ✅ SECURITY: No subscription ID passed - retrieved server-side from tenants table
        const response = await (apiService as any).cancelSubscription();
         if (response.success) {
           setUnsubscribeSuccess(true);
           setShowUnsubscribeModal(false);
           telemetryService.capture('subscription_cancelled', 'subscription-card', {
             properties: {
               plan: stats?.plan || 'unknown',
             }
           });
          
          // Reload stats after successful cancellation
          setTimeout(() => {
            loadUsageStats();
            loadTenantData();
          }, 1500);
        } else {
          setError(response.error || t('subscription.unsubscribeModal.errorUnsubscribe'));
        }
      } catch (err) {
        setError(t('subscription.unsubscribeModal.errorUnsubscribe'));
        console.error('Unsubscribe error:', err);
      } finally {
        setUnsubscribing(false);
      }
   };

  const handleMarketplaceClick = async () => {
    try {
      const response = await (apiService as any).getMarketplaceUrl();
      if (response.success && response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (err) {
      console.error('Failed to get marketplace URL:', err);
    }
  };

   const hasActiveStripeSubscription =
     tenantData?.stripeSubscriptionId &&
     tenantData?.subscriptionStatus === 'active';

   // ✨ Check if non-Stripe license (BYOL) is active AND not expired
   const hasActiveNonStripeSubscription =
     tenantData?.subscriptionStatus === 'active' &&
     stats?.isStripeSubscription === false &&
     stats?.validUntil &&
     new Date(stats.validUntil) >= new Date();

  const handleManageStripeSubscription = async () => {
    setLoadingPortal(true);
    try {
      const response = await (apiService as any).createStripeCustomerPortalSession();
      if (response.success && response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (err) {
      console.error('Failed to open Stripe Customer Portal:', err);
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleSubscribeClick = async () => {
    setSubscribing(true);
    setError(null);
    telemetryService.capture('subscription_stripe_checkout_initiated', 'subscription-card', {
      properties: {
        plan_id: 'web-checkout',
        billing_cycle: 'monthly',
      }
    });
    try {
      const response = await (apiService as any).createLicenseRequest();
      if (response.success && response.data) {
        const { requestId, redirectUrl } = response.data;
        const url = redirectUrl || `https://www.shawork.com/checkout?requestId=${requestId}`;

        // Open the checkout page in the user's default browser via Teams SDK
        try {
          const teamsJs = await import('@microsoft/teams-js');
          try { await teamsJs.app.initialize(); } catch {}
          console.log('[SubscriptionCard] teamsJs.app.openLink : ', url);
          await teamsJs.app.openLink(url);
        } catch (teamsErr) {
          console.warn('[SubscriptionCard] teamsJs.app.openLink failed, falling back to window.open:', teamsErr);
          window.open(url, '_blank');
        }
      } else {
        console.error('Failed to create license request:', response.error);
        setError(response.error || t('subscription.licenseActivation.errorActivation'));
      }
    } catch (err) {
      console.error('Error creating license request:', err);
      setError(t('subscription.licenseActivation.errorActivation'));
    } finally {
      setSubscribing(false);
    }
  };

   const formatDate = (dateStr: string) => {
     const date = new Date(dateStr);
     const locale = t('_locale', { defaultValue: 'en-US' });
     return date.toLocaleDateString(locale, { 
       day: 'numeric', 
       month: 'long', 
       year: 'numeric' 
     });
   };

   if (loading) {
     return (
       <div className="subscription-card">
         <div className="subscription-card-header">
           <h2>{t('subscription.title')}</h2>
         </div>
         <div className="subscription-loading">
           <div className="spinner-small"></div>
           <p>{t('subscription.loading')}</p>
         </div>
       </div>
     );
   }

  if (!stats) {
    return null;
  }

  const isUnlimited = stats.currentMonth.limit === -1;
  const isNearLimit = stats.currentMonth.percentage >= 80;
  const isAtLimit = stats.currentMonth.percentage >= 100;
  const isPremium = stats.plan !== 'free';

  // Check if subscription is expired
  const isExpired = tenantData?.subscriptionStatus === 'expired' ||
    (stats.validUntil && new Date(stats.validUntil) < new Date());

   return (
     <div className="subscription-card">
       <div className="subscription-card-header">
         <h2>{t('subscription.title')}</h2>
       </div>

       {success && (
         <div className="subscription-success-banner">
           <span className="subscription-success-icon">✓</span>
           <span>{t('subscription.successBanner')}</span>
         </div>
       )}

       {tenantData?.subscriptionStatus === 'suspended' && (
         <div className="subscription-suspended-banner">
           <span className="subscription-suspended-icon">⚠️</span>
           <div className="subscription-suspended-content">
             <strong>{t('subscription.suspendedBanner.title')}</strong>
             <p>{t('subscription.suspendedBanner.message')}</p>
           </div>
         </div>
       )}

       {isExpired && (
         <div className="subscription-expired-banner">
           <span className="subscription-expired-icon">⏰</span>
           <div className="subscription-expired-content">
             <strong>{t('subscription.expiredBanner.title')}</strong>
             <p>{t('subscription.expiredBanner.message')}</p>
           </div>
         </div>
       )}

       <div className="subscription-card-content">
         {/* Left Section: Plan Status */}
         <div className="subscription-plan-section">
           <div className="subscription-plan-header">
             <h3>{t('subscription.currentPlan')}</h3>
             <div className="subscription-plan-badges">
               <span className={`subscription-badge subscription-badge--${stats.plan}`}>
                 {t(`subscription.planBadges.${stats.plan}`)}
               </span>
               {tenantData?.subscriptionStatus === 'suspended' && (
                 <span className="subscription-badge subscription-badge--suspended">
                   {t('subscription.planBadges.suspended')}
                 </span>
               )}
             </div>
           </div>

           {/* Display License Key and Expiration - BYOL only */}
           {currentLicenseKey && !tenantData?.subscriptionId && tenantData?.subscriptionStatus !== 'cancelled' && (
             <div className="subscription-license-info">
               <div className="subscription-license-key">
                 <label>{t('subscription.licenseInfo.byolLabel')}</label>
                 <code>{currentLicenseKey}</code>
               </div>
               {stats.validUntil && (
                 <div className="subscription-expiration-info">
                   <label>{t('subscription.licenseInfo.validUntil')}</label>
                   <span>{formatDate(stats.validUntil)}</span>
                 </div>
               )}
             </div>
           )}

           {/* Display Marketplace Subscription Info - Only if active (not cancelled) */}
           {tenantData?.subscriptionId && tenantData?.subscriptionStatus !== 'cancelled' && (
             <div className="subscription-marketplace-info">
               <div className="subscription-marketplace-id">
                 <label>{t('subscription.licenseInfo.marketplaceLabel')}</label>
                 <code>{tenantData.subscriptionId}</code>
               </div>
               {stats.validUntil && (
                 <div className="subscription-expiration-info">
                   <label>{t('subscription.licenseInfo.validUntil')}</label>
                   <span>{formatDate(stats.validUntil)}</span>
                 </div>
               )}
             </div>
           )}

           {!isUnlimited ? (
             <div className="subscription-usage-info">
               <div className="subscription-usage-text">
                 <strong>{stats.currentMonth.used} / {stats.currentMonth.limit} {t('subscription.usageInfo.requests')}</strong>
                 <span className="subscription-usage-reset">
                   {t('subscription.usageInfo.renewalDate', { date: formatDate(stats.resetDate) })}
                 </span>
               </div>

               <div className="subscription-progress-container">
                 <div className="subscription-progress-bar">
                   <div
                     className={`subscription-progress-fill ${
                       isAtLimit ? 'at-limit' : isNearLimit ? 'near-limit' : ''
                     }`}
                     style={{
                       width: `${Math.min(stats.currentMonth.percentage, 100)}%`
                     }}
                   />
                 </div>
                 <span className="subscription-progress-percentage">
                   {stats.currentMonth.percentage}%
                 </span>
               </div>

               {isAtLimit && (
                 <div className="subscription-warning">
                   {t('subscription.usageInfo.limitReached')}
                 </div>
               )}
             </div>
           ) : !isExpired && tenantData?.subscriptionStatus !== 'suspended' ? (
             <div className="subscription-unlimited">
               <div className="subscription-unlimited-badge">{t('subscription.unlimited.badge')}</div>
               <p className="subscription-unlimited-text">
                 {t('subscription.unlimited.text')}
               </p>
               {stats.validUntil && (
                 <p className="subscription-expiration">
                   {t('subscription.unlimited.validUntil', { date: formatDate(stats.validUntil) })}
                 </p>
               )}
             </div>
           ) : (
             <div className="subscription-downgrade-action">
               <div className="subscription-downgrade-icon">{t('subscription.downgrade.icon')}</div>
               <div className="subscription-downgrade-content">
                 <h4>{t('subscription.downgrade.title')}</h4>
                 <p>{t('subscription.downgrade.message', { status: isExpired ? t('subscription.downgrade.messageExpired') : t('subscription.downgrade.messageSuspended') })}</p>
               </div>
               <button
                 onClick={() => setShowUnsubscribeModal(true)}
                 disabled={unsubscribing}
                 className="subscription-downgrade-btn"
                 title={t('subscription.downgrade.button')}
               >
                 {unsubscribing ? (
                   <>
                     <span className="spinner-tiny"></span>
                     <span>{t('subscription.unsubscribeModal.processingButton')}</span>
                   </>
                 ) : (
                   t('subscription.downgrade.button')
                 )}
               </button>
             </div>
           )}
        </div>

         {/* Right Section: License Activation */}
         <div className="subscription-license-section">
           <h3>{t('subscription.licenseActivation.title')}</h3>

           <div className="subscription-input-group">
             <input
               type="text"
               placeholder={t('subscription.licenseActivation.placeholder')}
               value={licenseKey}
               onChange={(e) => {
                 setLicenseKey(e.target.value);
                 setError(null);
               }}
               disabled={activating || success}
               className="subscription-input"
               aria-label={t('subscription.licenseActivation.inputLabel')}
             />
             <button
               onClick={handleActivateLicense}
               disabled={activating || success || !licenseKey.trim()}
               className="subscription-activate-btn"
               aria-label={t('subscription.licenseActivation.activateButton')}
             >
               {activating ? (
                 <>
                   <span className="spinner-tiny"></span>
                   <span>{t('subscription.licenseActivation.activatingButton')}</span>
                 </>
               ) : (
                 t('subscription.licenseActivation.activateButton')
               )}
             </button>
           </div>

           {error && (
             <div className="subscription-error">
               {error}
             </div>
           )}

           <div className="subscription-divider">
             <span>{t('subscription.obtainLicense.divider')}</span>
           </div>

               <div className="subscription-links">
                  {isDesktop && (
                  <button
                    onClick={hasActiveStripeSubscription ? handleManageStripeSubscription : handleSubscribeClick}
                   className="subscription-link-item"
                   disabled={loadingPortal || subscribing || hasActiveNonStripeSubscription}
                   title={hasActiveNonStripeSubscription 
                     ? t('subscription.stripe.byolActiveTooltip')
                     : t(hasActiveStripeSubscription
                       ? 'subscription.stripe.manageTooltip'
                       : 'subscription.stripe.subscribeTooltip')}
                 >
                   <span className="subscription-link-icon">💳</span>
                   <div className="subscription-link-content">
                     <div className="subscription-link-title">
                       {t(hasActiveStripeSubscription
                         ? 'subscription.stripe.manageTitle'
                         : 'subscription.stripe.subscribeTitle')}
                     </div>
                     <div className="subscription-link-desc">
                       {t(hasActiveStripeSubscription
                         ? 'subscription.stripe.manageDescription'
                         : 'subscription.stripe.subscribeDescription')}
                     </div>
                   </div>
                    {subscribing ? (
                      <span className="spinner-tiny"></span>
                    ) : (
                      <span className="subscription-link-arrow">→</span>
                    )}
                  </button>
                  )}

                {isDesktop && hasActiveNonStripeSubscription && (
                  <div className="subscription-stripe-disabled-message">
                    <p>{t('subscription.stripe.byolActiveMessage')}</p>
                  </div>
                )}

              <a
                href={`mailto:contact@shawork.com?subject=${t('subscription.obtainLicense.contact.emailSubject')}&body=${t('subscription.obtainLicense.contact.emailBody')}`}
                className="subscription-link-item"
                title={t('subscription.obtainLicense.contact.tooltip')}
              >
                <span className="subscription-link-icon">✉️</span>
                <div className="subscription-link-content">
                  <div className="subscription-link-title">{t('subscription.obtainLicense.contact.title')}</div>
                  <div className="subscription-link-desc">{t('subscription.obtainLicense.contact.description')}</div>
                </div>
                <span className="subscription-link-arrow">→</span>
              </a>
            </div>
         </div>
      </div>

       {/* Unsubscribe Confirmation Modal */}
       <Dialog open={showUnsubscribeModal} onOpenChange={(_, data) => !data.open && setShowUnsubscribeModal(false)}>
         <DialogSurface style={{ maxWidth: '500px' }}>
           <DialogBody>
             <DialogTitle>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                 <span>{t('subscription.unsubscribeModal.title')}</span>
                 <Button
                   appearance="subtle"
                   icon={<Dismiss24Regular />}
                   onClick={() => setShowUnsubscribeModal(false)}
                 />
               </div>
             </DialogTitle>
             <DialogContent>
               <div style={{ padding: '16px 0' }}>
                 <Text as="p" style={{ marginBottom: '16px' }}>
                   {t('subscription.unsubscribeModal.question')}
                 </Text>
                 <div style={{
                   backgroundColor: tokens.colorPaletteYellowBackground1,
                   padding: '12px',
                   borderRadius: '6px',
                   marginBottom: '16px'
                 }}>
                   <Text size={200} weight="semibold">
                     {t('subscription.unsubscribeModal.warning')}
                   </Text>
                   <Text as="p" size={200} style={{ marginTop: '8px' }}>
                     {t('subscription.unsubscribeModal.warningDetails')}
                   </Text>
                 </div>
               </div>
             </DialogContent>
             <DialogActions>
               <Button
                 appearance="secondary"
                 onClick={() => setShowUnsubscribeModal(false)}
               >
                 {t('subscription.unsubscribeModal.cancelButton')}
               </Button>
               <Button
                 appearance="primary"
                 onClick={handleUnsubscribe}
                 disabled={unsubscribing}
               >
                 {unsubscribing ? (
                   <>
                     <span style={{ marginRight: '8px' }}>⏳</span>
                     {t('subscription.unsubscribeModal.processingButton')}
                   </>
                 ) : (
                   t('subscription.unsubscribeModal.confirmButton')
                 )}
               </Button>
             </DialogActions>
           </DialogBody>
         </DialogSurface>
       </Dialog>
    </div>
  );
};
